import styles from "css/components/tables/generic-table.module.css"
import { ReactElement, ReactNode, useState, useEffect } from "react";

/* 'roster' is the student-roster treatment from the one mockup that draws a
   numbered list of people (AllForOne_ClassroomDetail_v2.html's `.who-cell`):
   "1  Amanda Loeb" -- no period, the number set quieter and narrower than the
   shared ordinal, the name carrying the weight, and the two baseline-aligned.

   A variant rather than a pile of style props, and 'default' leaves every
   existing caller byte-identical. It lives here because this component owns
   the markup: styling it from a consuming page would mean reaching through
   hashed module class names, and the name is sometimes wrapped in a <Link>,
   so there is no stable element path to target from outside either. */
export const NumberedView = ({ index, value, variant = 'default' }: { index: number, value: ReactNode, variant?: 'default' | 'roster' }) => {
    const roster = variant === 'roster'
    return (
        <div className={`${styles.value} ${roster ? styles.valueRoster : ''}`}>
            <span className={`${styles.index} ${roster ? styles.indexRoster : ''}`}>
                {index + 1}{roster ? '' : '. '}
            </span>
            <span className={roster ? styles.nameRoster : ''}>{value}</span>
        </div>
    )
}

type GenericTableProps = {
    style: any,
    columns: any,
    data: any,
    rowKey: string,
    renderHeader?: boolean,
    renderMoreActions?: boolean,
    createMoreActionCell?: any,
    rowClassName?: any
    doubleClickRowAction?: any
};

const Row = ({ row, idx, rowKey, rowClassName, doubleClickRowAction, columns, renderMoreActions, createMoreActionCell, isSelected, onRowSelect, disabled = false }) => {
    return (
        <tr
            key={row[rowKey]}
            className={`${rowClassName ? rowClassName(row, idx) : ''} ${isSelected ? styles.selectedRow : ''}`}
            onDoubleClick={() => doubleClickRowAction(row[rowKey])}
            onClick={() => onRowSelect(row[rowKey])}
            style={{ opacity: disabled ? 0.5 : 1 }}
        >
            {columns.map(col => (
                <td key={col.key} className={col.className}>
                    {col.render ? col.render(row[col.key], idx, row) : row[col.key]}
                </td>
            ))}
            {renderMoreActions && (
                createMoreActionCell(row[rowKey], isSelected)
            )}
        </tr>
    )
}

// TODO: Rename the style prop to be more specific
export const GenericTable = ({
    style,
    columns,
    data,
    rowKey,
    renderHeader,
    renderMoreActions,
    createMoreActionCell,
    rowClassName,
    doubleClickRowAction
}: GenericTableProps) => {
    const [selectedRowKey, setSelectedRowKey] = useState(null);

    // Handle clicking outside the table to unhighlight
    useEffect(() => {
        const handleClickOutside = (event) => {
            const tableContainer = event.target.closest(`.${styles.genericTableContainer}`);
            if (!tableContainer) {
                setSelectedRowKey(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const handleRowSelect = (rowKey) => {
        setSelectedRowKey(selectedRowKey === rowKey ? null : rowKey);
    };

    return (
        <div className={styles.genericTableContainer}>
            <table>
                {renderHeader && <thead>
                    <tr>
                        {columns.map(col => <th key={col.key}>{col.header}</th>)}
                        {renderMoreActions && <th></th>}
                    </tr>
                </thead>
                }
                <tbody style={style}>
                    {data.map((row, idx) => {
                        if (row.isDuplicating) {
                            return (
                                <Row
                                    key={row[rowKey]}
                                    row={row}
                                    idx={idx}
                                    rowKey={rowKey}
                                    rowClassName={rowClassName}
                                    doubleClickRowAction={doubleClickRowAction}
                                    columns={columns}
                                    renderMoreActions={renderMoreActions}
                                    createMoreActionCell={createMoreActionCell}
                                    isSelected={selectedRowKey === row[rowKey]}
                                    onRowSelect={handleRowSelect}
                                    disabled={true}
                                />
                            )
                        }
                        return (
                            <Row
                                key={row[rowKey]}
                                row={row}
                                idx={idx}
                                rowKey={rowKey}
                                rowClassName={rowClassName}
                                doubleClickRowAction={doubleClickRowAction}
                                columns={columns}
                                renderMoreActions={renderMoreActions}
                                createMoreActionCell={createMoreActionCell}
                                isSelected={selectedRowKey === row[rowKey]}
                                onRowSelect={handleRowSelect}
                            />
                        )})}
                </tbody>
            </table>
        </div>
    );
};